import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"];
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Form } from 'foundations-components/transitional/components/Form';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import { useEffect, useState } from 'react';
import { Form as FFForm } from 'react-final-form';
import isEqual from 'hs-lodash/isEqual';
import { Header } from '../../components/Header';
import { getProductList } from '../../data/productsUsed';
import { useUpdateUserProfile, useCreateUserProfile } from '../../data/userProfile';
import FFInputControl from './fields/FFInputControl';
import FFInputStaticLabelControl from './fields/FFInputStaticLabelControl';
import FFSelectMultiControl from './fields/FFSelectMultiControl';
import FFTextareaControl from './fields/FFTextareaControl';
import { composeValidators, isValidCharacters } from './validators';
import { Spacer } from 'foundations-components/components/Spacer';
import { isValidLength } from './validators';
import FFSelectControl from './fields/FFSelectControl';
import { industryOptions } from './options';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import FFToggle from './fields/FFToggle';
import { useMediaQuery } from '../../../../utils/Styling';
import styled, { css } from 'foundations-theming/utils';
import { FixedSaveBar } from 'foundations-components/transitional/components/FixedSaveBar';
import { Button } from 'foundations-components/transitional/components/Button';
import { useHistory } from 'quartz-routing/react-router';
import { Box } from 'foundations-components/transitional/components/Box';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { SaveEditableViewIcon } from 'foundations-assets/icons/components/SaveEditableViewIcon';
import { UndoIcon } from 'foundations-assets/icons/components/UndoIcon';
import { H4 } from 'foundations-components/transitional/components/H4';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const extractFieldValuesFromProfile = profile => ({
  avatarImage: undefined,
  biography: profile.biography,
  companyName: profile.companyName,
  displayName: profile.displayName,
  githubId: profile.githubId,
  industryCodeId: profile.industryCodeId,
  jobTitle: profile.jobTitle || '',
  linkedinId: profile.linkedinId,
  meetingsId: profile.meetingsId,
  pronouns: profile.pronouns,
  public: profile.public,
  twitterId: profile.twitterId,
  userName: profile.userName,
  products: profile.products || []
});
const extractFieldValuesFromForm = values => ({
  biography: values.biography || '',
  companyName: values.companyName || '',
  displayName: values.displayName,
  githubId: values.githubId || '',
  industryCodeId: values.industryCodeId || null,
  jobTitle: values.jobTitle || '',
  linkedinId: values.linkedinId || '',
  meetingsId: values.meetingsId || '',
  pronouns: values.pronouns || '',
  public: values.public,
  twitterId: values.twitterId || '',
  userName: values.userName,
  products: values.products || []
});
const FormSection = _ref => {
  let {
      children
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    isTablet
  } = useMediaQuery();
  return /*#__PURE__*/_jsx(View, Object.assign({
    backgroundColor: "fill-surface-default",
    borderRadius: "100",
    boxShadow: "100",
    padding: isTablet ? '600' : '1000'
  }, props, {
    children: children
  }));
};
const FormGrid = styled.div.withConfig({
  displayName: "EditProfileForm__FormGrid"
})(["display:grid;grid-template-columns:auto auto;gap:", ";width:100%;", " .expandToFullWidth{grid-column:1 / span 2;", "}"], ({
  theme
}) => theme.space['400'], ({
  $isMobile
}) => $isMobile && css(["grid-template-columns:1fr;"]), ({
  $isMobile
}) => $isMobile && css(["grid-column:auto;"]));
const showEditSuccessNotification = () => {
  FloatingAlertStore.addAlert({
    message: I18n.text('profilePage.editProfile.notification.success.message'),
    titleText: I18n.text('profilePage.editProfile.notification.success.title'),
    type: 'success'
  });
};
const showErrorNotification = () => {
  FloatingAlertStore.addAlert({
    message: I18n.text('profilePage.editProfile.notification.error.message'),
    titleText: I18n.text('profilePage.editProfile.notification.error.title'),
    type: 'danger'
  });
};
export const EditProfileForm = ({
  authedUser,
  trackPageView,
  trackInteraction,
  successMarker,
  userProfile,
  showHeader = true,
  isCreating = false,
  isHubless = false
}) => {
  const {
    isMobile,
    isTablet
  } = useMediaQuery();
  useEffect(() => {
    trackPageView();
  }, [trackPageView]);
  const [initialValues, setInitialValues] = useState(extractFieldValuesFromProfile(userProfile));
  const [updateUserProfile, {
    loading: updateLoading
  }] = useUpdateUserProfile();
  const [createUserProfile, {
    loading: createLoading
  }] = useCreateUserProfile();
  const profileLoading = isCreating ? createLoading : updateLoading;
  const handleCreateProfile = async profileData => {
    await createUserProfile({
      variables: profileData
    });
    showEditSuccessNotification();
  };
  const handleUpdateProfile = async profileData => {
    await updateUserProfile({
      variables: profileData
    });
    showEditSuccessNotification();
  };
  const onSubmit = async values => {
    try {
      const newValues = extractFieldValuesFromForm(values);
      if (isCreating) {
        await handleCreateProfile(newValues);
      } else {
        await handleUpdateProfile(newValues);
      }

      // Update initial values to current form state after successful save
      // This makes the form appear "clean" and hides the FixedSaveBar
      setInitialValues(values);
    } catch (err) {
      showErrorNotification();
    }
  };
  const history = useHistory();
  if (!(authedUser !== null && authedUser !== void 0 && authedUser.id)) {
    return /*#__PURE__*/_jsx(ErrorMessage, {
      children: /*#__PURE__*/_jsx(Text, {
        as: "p",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "profilePage.editProfile.notFound"
        })
      })
    });
  }
  return /*#__PURE__*/_jsxs(View, {
    children: [successMarker, /*#__PURE__*/_jsx(FFForm, {
      initialValues: initialValues,
      onSubmit: onSubmit,
      render: ({
        handleSubmit,
        values,
        form
      }) => {
        const hasFormChanged = !isEqual(extractFieldValuesFromForm(initialValues), extractFieldValuesFromForm(values));
        return /*#__PURE__*/_jsxs(Form, {
          onSubmit: e => void handleSubmit(e),
          children: [showHeader && /*#__PURE__*/_jsx(Header, {}), /*#__PURE__*/_jsx(_Fragment, {
            children: /*#__PURE__*/_jsxs(Flex, {
              flexDirection: "column",
              gap: "400",
              inlineSize: "100%",
              margin: "auto",
              maxInlineSize: "1300px" // 1280px + 20px padding
              ,
              paddingBlockEnd: "1500",
              paddingBlockStart: "0",
              paddingInline: isHubless ? '400' : '0',
              children: [/*#__PURE__*/_jsx(FormSection, {
                paddingBlock: "400",
                paddingInline: isTablet ? '600' : '1000',
                children: /*#__PURE__*/_jsxs(Flex, {
                  alignItems: "center",
                  justifyContent: "space-between",
                  children: [/*#__PURE__*/_jsx(Box, {
                    basis: 0,
                    inlineSize: "0",
                    children: /*#__PURE__*/_jsxs(Button
                    // @ts-ignore - minInlineSize can accept a string
                    , {
                      minInlineSize: "max-content",
                      onClick: () => history.goBack(),
                      responsive: false,
                      use: "tertiary-extra-light",
                      children: [/*#__PURE__*/_jsx(LeftIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "profilePage.header.back"
                      })]
                    })
                  }), !isMobile && /*#__PURE__*/_jsx(Box, {
                    grow: 1,
                    pointerEvents: "none",
                    textAlign: "center",
                    children: /*#__PURE__*/_jsx(H4, {
                      marginBlockEnd: "0",
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "profilePage.editProfile.title"
                      })
                    })
                  })]
                })
              }), /*#__PURE__*/_jsxs(FormSection, {
                children: [/*#__PURE__*/_jsx(Text, {
                  as: "h4",
                  marginBlockEnd: "600",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "profilePage.editProfile.personalDetails.title"
                  })
                }), /*#__PURE__*/_jsxs(FormGrid, {
                  $isMobile: isMobile,
                  children: [/*#__PURE__*/_jsx(FFInputControl, {
                    label: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "profilePage.editProfile.personalDetails.displayName.label"
                    }),
                    name: "displayName",
                    validate: composeValidators(isValidLength(250))
                  }), /*#__PURE__*/_jsx(FFInputControl, {
                    label: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "profilePage.editProfile.personalDetails.pronouns.label"
                    }),
                    name: "pronouns",
                    validate: composeValidators(isValidLength(250)),
                    verticalSeparation: "flush"
                  }), /*#__PURE__*/_jsx("div", {
                    className: "expandToFullWidth",
                    children: /*#__PURE__*/_jsx(FFTextareaControl, {
                      label: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "profilePage.editProfile.personalDetails.bio.label"
                      }),
                      name: "biography",
                      placeholder: I18n.text('profilePage.editProfile.personalDetails.bio.placeholder'),
                      validate: composeValidators(isValidLength(250))
                    })
                  }), /*#__PURE__*/_jsx(FFInputControl, {
                    label: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "profilePage.editProfile.personalDetails.jobTitle.label"
                    }),
                    name: "jobTitle",
                    validate: composeValidators(isValidLength(250))
                  }), /*#__PURE__*/_jsx(FFInputControl, {
                    label: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "profilePage.editProfile.personalDetails.companyName.label"
                    }),
                    name: "companyName",
                    validate: composeValidators(isValidLength(250))
                  }), /*#__PURE__*/_jsx(FFSelectControl, {
                    clearable: true,
                    label: I18n.text('profilePage.editProfile.personalDetails.industry.label'),
                    name: "industryCodeId",
                    options: industryOptions(),
                    placeholder: I18n.text('profilePage.editProfile.personalDetails.industry.placeholder')
                  })]
                })]
              }), /*#__PURE__*/_jsxs(FormSection, {
                "data-test-id": "social-links-box",
                children: [/*#__PURE__*/_jsx(Text, {
                  as: "h4",
                  marginBlockEnd: "600",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "profilePage.editProfile.socialLinks.title"
                  })
                }), /*#__PURE__*/_jsxs(Flex, {
                  flexDirection: "column",
                  gap: "400",
                  children: [/*#__PURE__*/_jsx(FFInputStaticLabelControl, {
                    label: "https://www.linkedin.com/in/",
                    name: "linkedinId",
                    validate: composeValidators(isValidLength(250), isValidCharacters)
                  }), /*#__PURE__*/_jsx(FFInputStaticLabelControl, {
                    label: "https://github.com/",
                    name: "githubId",
                    validate: composeValidators(isValidLength(250), isValidCharacters)
                  }), /*#__PURE__*/_jsx(FFInputStaticLabelControl, {
                    label: "https://x.com/",
                    name: "twitterId",
                    validate: composeValidators(isValidLength(250), isValidCharacters)
                  }), /*#__PURE__*/_jsx(FFInputStaticLabelControl, {
                    label: "https://meetings.hubspot.com/",
                    name: "meetingsId",
                    validate: composeValidators(isValidLength(250), isValidCharacters)
                  })]
                })]
              }), /*#__PURE__*/_jsxs(FormSection, {
                children: [/*#__PURE__*/_jsx(Text, {
                  as: "h4",
                  marginBlockEnd: "600",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "profilePage.editProfile.productsUsed.title"
                  })
                }), /*#__PURE__*/_jsx(FFSelectMultiControl, {
                  label: I18n.text('profilePage.editProfile.productsUsed.formControl.label'),
                  name: "products",
                  options: getProductList(),
                  placeholder: I18n.text('profilePage.editProfile.productsUsed.formControl.placeholder')
                })]
              }), /*#__PURE__*/_jsxs(FormSection, {
                children: [/*#__PURE__*/_jsx(Text, {
                  as: "h4",
                  marginBlockEnd: "600",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "profilePage.editProfile.visibility.title"
                  })
                }), /*#__PURE__*/_jsx(FFToggle, {
                  inputProps: {
                    size: 'small'
                  },
                  label: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "profilePage.editProfile.visibility.formControl.label"
                  }),
                  name: "public"
                })]
              }), /*#__PURE__*/_jsx(Spacer, {
                space: "600"
              })]
            })
          }), hasFormChanged && /*#__PURE__*/_jsxs(FixedSaveBar, {
            textAlign: "center",
            children: [/*#__PURE__*/_jsxs(LoadingButton, {
              loading: profileLoading,
              onClick: e => {
                handleSubmit(e);
                trackInteraction('click save profile button');
              },
              use: "primary",
              children: [/*#__PURE__*/_jsx(SaveEditableViewIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
                message: "profilePage.editProfile.saveButton"
              })]
            }), /*#__PURE__*/_jsxs(Button, {
              onClick: () => form.reset(),
              use: "tertiary-light",
              children: [/*#__PURE__*/_jsx(UndoIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
                message: "profilePage.editProfile.undoChanges"
              })]
            })]
          })]
        });
      }
    })]
  });
};